import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import { createTheme, lighten } from '@mui/material';

export const theme = createTheme({
    components: {
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    zIndex: 100,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                variant: 'contained',
            },
            styleOverrides: {
                contained: {
                    minWidth: 0,
                    padding: '6px 10px',
                },
                sizeLarge: {
                    borderRadius: 100,
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 24,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    padding: '16px 48px',
                },
                text: {
                    fontSize: 14,
                    minWidth: 0,
                    padding: 0,
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                '.game': {
                    display: 'block',
                    '& ~ div': {
                        left: '0 !important',
                        top: '0 !important',
                    },
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    padding: 40,
                },
            },
        },
        MuiIconButton: {
            defaultProps: {
                color: 'primary',
                size: 'small',
            },
            styleOverrides: {
                root: {
                    '.map-controls &': {
                        '&:disabled': {
                            color: 'inherit',
                            opacity: 0.5,
                        },
                        '&:hover': {
                            color: lighten('#3F51B5', 0.2),
                        },
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                colorSecondary: {
                    color: 'rgba(255, 255, 255, 0.87)',
                    fontSize: 18,
                    '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover fieldset': {
                        borderColor: '#ebebeb !important',
                    },
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                size: 'small',
                variant: 'outlined',
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: 12,
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                size: 'small',
                variant: 'outlined',
            },
        },
        MuiToggleButton: {
            defaultProps: {
                sx: {
                    borderRadius: 4,
                    padding: 2.5,
                },
            },
            styleOverrides: {
                root: {
                    backgroundColor: '#FAFAFA',
                    border: 0,
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: 16,
                    fontWeight: 700,
                    '&.Mui-disabled, &.Mui-selected': {
                        border: 0,
                    },
                },
            },
        },
        MuiToggleButtonGroup: {
            defaultProps: {
                sx: {
                    borderRadius: 4,
                    boxShadow: 1,
                },
            },
            styleOverrides: {
                grouped: {
                    '&:not(:first-of-type)': {
                        borderColor: '#DADADA',
                    },
                },
                root: {
                    backgroundColor: '#F0F0F0',
                    border: 0,
                },
            },
        },
    },
    palette: {
        action: {
            disabled: '#ACB5BD',
        },
        error: {
            main: '#EF3F25',
        },
        grey: {
            '100': '#FAFAFA',
            '200': '#F0F0F0',
            '300': '#EEF0F6',
            '400': '#DFDFDF',
            '500': '#DADADA',
            '600': '#C4C4C4',
            '700': '#ACB5BD',
            '800': '#6F6F6F',
            // '900': '#212429',
        },
        primary: {
            main: '#3F51B5',
        },
        secondary: {
            dark: '#FFB98E',
            light: '#FEE6BA',
            main: '#F9C132',
        },
        success: {
            main: '#579C40',
        },
    },
    typography: {
        // Defaults
        body1: {
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.17,
        },
        button: {
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: 0.5,
            lineHeight: 1.17,
        },
        fontFamily: 'Roboto, sans-serif',

        // Montserrat
        montserrat410: {
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 10,
            fontWeight: 400,
            lineHeight: 1.5,
        },
        montserrat414: {
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.2,
        },
        montserrat416: {
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.625,
        },
        montserrat514: {
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.43,
        },
        montserrat518: {
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 18,
            fontWeight: 500,
            lineHeight: 1.2,
        },
        montserrat618: {
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 1.22,
        },
        montserrat720: {
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.22,
        },
        montserrat740: {
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 40,
            fontWeight: 700,
            lineHeight: 1.22,
        },

        // Roboto
        roboto412: {
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.17,
        },
        roboto414: {
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.33,
        },
        roboto416: {
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.17,
        },
        roboto418: {
            fontSize: 18,
            fontWeight: 400,
        },
        roboto420: {
            fontSize: 20,
            fontWeight: 400,
        },
        roboto424: {
            fontSize: 24,
            fontWeight: 400,
            lineHeight: 1.17,
        },
        roboto428: {
            fontSize: 28,
            fontWeight: 400,
            lineHeight: 1.15,
        },
        roboto518: {
            fontSize: 18,
            fontWeight: 500,
            lineHeight: 1.17,
        },
        roboto524: {
            fontSize: 24,
            fontWeight: 500,
            lineHeight: 1.17,
        },
        roboto718: {
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.17,
        },
        roboto721: {
            fontSize: 21,
            fontWeight: 700,
            lineHeight: 1.17,
        },
        roboto728: {
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1.17,
        },
    },
});
